import React, { useEffect, useState } from 'react';
import { Clock, Leaf, AlertCircle, MapPin, Utensils, Sparkles, Loader2, Tag, Phone, Mail, CreditCard } from 'lucide-react';
import { images, restaurantInfo, weeklyMenu } from '../data/mock';
import { SummerBreakCard } from '../components/SummerBreakNotice';

const MENU_EMBED_URL =
  'https://lunch-cms-simple.emergent.host/api/embed/menu?restaurant=69ebd2e5d9b331f59d6d7b03&color=%2310b981';

// ISO-viikkonumero (maanantai = viikon 1. päivä)
const getIsoWeek = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// Suomalainen viikonpäivä tänään (Maanantai..Sunnuntai). Palauttaa null viikonloppuisin.
const getTodayFiWeekday = () => {
  const names = ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'];
  const idx = new Date().getDay();
  if (idx === 0 || idx === 6) return null;
  return names[idx];
};

// Fetchaa ruokapaikka.fi-tyyliseltä CMS:ltä HTML-ruokalistan ja parsii siitä päivät + ruokalajit
const parseMenuHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const description = doc.querySelector('p')?.textContent?.trim() || '';
  // Päiväblokit: div jonka sisällä on h3 ja ul
  const dayBlocks = Array.from(doc.querySelectorAll('div')).filter(
    (d) => d.querySelector(':scope > h3') && d.querySelector(':scope > ul')
  );
  const days = dayBlocks.map((block) => ({
    day: block.querySelector('h3')?.textContent?.trim() || '',
    items: Array.from(block.querySelectorAll('li'))
      .map((li) => li.textContent.trim())
      .filter(Boolean),
  }));
  return { description, days };
};

const LounasPage = () => {
  const [menu, setMenu] = useState({ description: '', days: [] });
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const weekNumber = getIsoWeek();
  const today = getTodayFiWeekday();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(MENU_EMBED_URL, { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('Menu fetch failed');
        const html = await res.text();
        const parsed = parseMenuHtml(html);
        if (!cancelled) {
          setMenu(parsed);
          setStatus(parsed.days.length > 0 ? 'ok' : 'error');
        }
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fallback mock-datalle jos haku epäonnistuu
  const showMock = status === 'error';
  const daysToRender = showMock
    ? weeklyMenu.days.map((d) => ({
        day: d.day,
        date: d.date,
        items: [d.main, d.soup].filter(Boolean),
      }))
    : menu.days;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Hero - Playful */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800 overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-10 left-10 w-32 h-32 border-4 border-emerald-500/20 rounded-full" />
        <div className="absolute bottom-10 right-10 w-48 h-48 border-4 border-emerald-500/10 rounded-full" />
        
        <div className="absolute inset-0 opacity-20">
          <img
            src={images.hotDishes}
            alt="Lounas"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="font-handwritten text-4xl text-emerald-400 block mb-2">Päivän ruoka</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Lounas
          </h1>
          <p className="text-lg text-stone-300 max-w-2xl mx-auto">
            Rehtiä kotiruokaa omasta keittiöstä. 
            <span className="font-handwritten text-xl text-emerald-400"> Meillä lounas maistuu aina!</span>
          </p>
        </div>
      </section>

      {/* Lunch Info Bar - Fun */}
      <section className="bg-emerald-500 text-white py-4 relative">
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 text-sm md:text-base">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <Clock className="w-5 h-5" />
              <span><strong>Lounas:</strong> ma-pe {restaurantInfo.lunchHours.weekdays}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <MapPin className="w-5 h-5" />
              <span><strong>Resq:</strong> klo 15 asti</span>
            </div>
          </div>
        </div>
      </section>

      {/* Kesätauko-ilmoitus */}
      <SummerBreakCard />

      {/* Menu Section - Modern Cards */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Viikko {weekNumber}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800">
              Viikon <span className="font-handwritten text-emerald-600">ruokalista</span>
            </h2>
          </div>

          {/* Brändin mukainen kuvaus + hintakortit (kovakoodattu) */}
          <div className="mb-12" data-testid="lunch-description">
            <div className="relative max-w-3xl mx-auto mb-8">
              {/* Koristepallot */}
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-emerald-100 rounded-full blur-xl opacity-70" />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-amber-100 rounded-full blur-xl opacity-70" />
              <div className="relative bg-white rounded-3xl p-6 sm:p-10 shadow-xl border-2 border-emerald-100">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="hidden sm:block h-px w-12 bg-emerald-300" />
                  <span className="font-handwritten text-2xl md:text-3xl text-emerald-600 text-center">
                    Mitä lautaselle?
                  </span>
                  <div className="hidden sm:block h-px w-12 bg-emerald-300" />
                </div>
                <p className="text-stone-700 leading-relaxed text-base md:text-lg text-center">
                  Lounasbuffet sisältää{' '}
                  <strong className="text-emerald-700">runsaan salaattipöydän</strong>, keiton ja{' '}
                  <strong className="text-emerald-700">kaksi lämmintä ruokaa</strong>, talon leivät,
                  kahvi/tee ja jälkiruoka.
                </p>
              </div>
            </div>

            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto"
              data-testid="lunch-prices"
            >
              {[
                { label: 'Lounas', price: '13€' },
                { label: 'Keitto + salaatti', price: '11€' },
                { label: 'Eläkeläiset, työttömät & opiskelijat', price: '12€' },
                {
                  label: 'Take away · Lounas',
                  price: '14€',
                  highlight: true,
                  href: `tel:${restaurantInfo.phone.replace(/\s+/g, '')}`,
                },
              ].map((p, i) => {
                const Wrapper = p.href ? 'a' : 'div';
                const wrapperProps = p.href
                  ? {
                      href: p.href,
                      'data-testid': 'takeaway-call-button',
                      'aria-label': `Tilaa take away soittamalla ${restaurantInfo.phone}`,
                    }
                  : {};
                return (
                  <Wrapper
                    key={i}
                    {...wrapperProps}
                    className={`relative rounded-2xl p-5 shadow-lg transform transition-all block ${
                      p.href
                        ? 'cursor-pointer hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98]'
                        : 'hover:-translate-y-1'
                    } ${
                      p.highlight
                        ? 'bg-stone-800 text-white border-2 border-emerald-400 hover:border-emerald-300'
                        : 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white'
                    }`}
                  >
                    {p.highlight && (
                      <span className="absolute -top-2 right-3 bg-amber-400 text-stone-900 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-md">
                        {p.href ? 'Tilaa' : 'Mukaan'}
                      </span>
                    )}
                    <div
                      className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-2 ${
                        p.highlight ? 'text-emerald-300' : 'text-emerald-100'
                      }`}
                    >
                      <Tag className="w-3.5 h-3.5" />
                      {p.label}
                    </div>
                    <div className="flex items-end justify-between gap-2">
                      <div className="text-3xl font-bold">{p.price}</div>
                      {p.href && (
                        <span className="inline-flex items-center gap-1.5 bg-emerald-400 text-stone-900 text-xs font-bold px-3 py-1.5 rounded-full">
                          <Phone className="w-3.5 h-3.5" />
                          Soita
                        </span>
                      )}
                    </div>
                  </Wrapper>
                );
              })}
            </div>

            {/* Lounasedut-maininta */}
            <p
              className="flex items-center justify-center gap-2 text-stone-600 text-sm md:text-base text-center mt-5"
              data-testid="lunch-benefits-note"
            >
              <CreditCard className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Meillä käy myös <strong className="text-emerald-700">lounasedut</strong> maksuvälineenä.</span>
            </p>

            {/* Yrityslounassopimus-nosto */}
            <div
              className="max-w-3xl mx-auto mt-8 bg-stone-900 rounded-3xl p-6 md:p-8 relative overflow-hidden"
              data-testid="corporate-lunch-box"
            >
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-emerald-500/10 rounded-full" />
              <div className="relative text-center">
                <span className="font-handwritten text-2xl md:text-3xl text-emerald-400 block mb-1">
                  Yrityksille
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-5">
                  Kysy myös lounassopimuksia yritykselle!
                </h3>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={`mailto:${restaurantInfo.email}`}
                    data-testid="corporate-lunch-email"
                    className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {restaurantInfo.email}
                  </a>
                  <a
                    href={`tel:${restaurantInfo.phone.replace(/\s+/g, '')}`}
                    data-testid="corporate-lunch-phone"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {restaurantInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Grid - Playful cards */}
          {status === 'loading' ? (
            <div
              className="flex items-center justify-center py-16 text-emerald-600"
              data-testid="lunch-loading"
            >
              <Loader2 className="w-6 h-6 animate-spin mr-3" />
              <span className="font-medium">Ladataan ruokalistaa...</span>
            </div>
          ) : (
            <div className="space-y-4" data-testid="lunch-menu-list">
              {daysToRender.map((day, index) => {
                const isToday = today && day.day?.toLowerCase() === today.toLowerCase();
                return (
                  <div
                    key={index}
                    className={`relative rounded-2xl p-6 transition-all group card-hover ${
                      isToday
                        ? 'bg-gradient-to-br from-emerald-50 to-white shadow-2xl border-2 border-emerald-500 ring-4 ring-emerald-200/50'
                        : 'bg-white shadow-lg hover:shadow-xl border-2 border-emerald-100 hover:border-emerald-200'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`lunch-day-${index}`}
                    data-today={isToday ? 'true' : 'false'}
                  >
                    {isToday && (
                      <div
                        className="absolute -top-3 left-6 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-md flex items-center gap-1"
                        data-testid="today-badge"
                      >
                        <Sparkles className="w-3 h-3" />
                        Tänään tarjolla
                      </div>
                    )}
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      <div className="md:w-36 flex-shrink-0">
                        <div
                          className={`text-white px-4 py-2 rounded-xl inline-block transform transition-transform ${
                            isToday
                              ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 rotate-0 scale-105'
                              : 'bg-gradient-to-br from-emerald-500 to-emerald-500 -rotate-1 group-hover:rotate-0'
                          }`}
                        >
                          <p className="font-bold text-lg">{day.day}</p>
                          {day.date && <p className="text-emerald-100 text-sm">{day.date}</p>}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        {day.items.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 mt-1">
                              <Utensils className="w-3 h-3" />
                            </span>
                            <span className="text-stone-800 font-medium text-base md:text-lg">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Special Diets Section - Playful */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-emerald-200 rounded-3xl rotate-2" />
              <img
                src={images.specialDiets}
                alt="Erityisruokavaliot"
                className="relative rounded-2xl shadow-lg w-full border-4 border-white"
              />
            </div>
            <div>
              <span className="font-handwritten text-3xl text-emerald-500">Kaikille sopivaa</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6 mt-2">
                Erityisruokavaliot huomioitu
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Leaf className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Gluteeniton</h3>
                    <p className="text-stone-600 text-sm">Gluteenittomat vaihtoehdot saatavilla päivittäin.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-amber-50 p-4 rounded-xl border border-amber-100">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Leaf className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Laktoositon</h3>
                    <p className="text-stone-600 text-sm">Laktoosittomat vaihtoehdot löytyvät aina.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-stone-50 p-4 rounded-xl border border-stone-200">
                  <div className="w-12 h-12 bg-stone-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Muut ruokavaliot</h3>
                    <p className="text-stone-600 text-sm">Kerro meille tarpeesi – huomioimme ne!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works - Fun steps */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-stone-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="font-handwritten text-3xl text-emerald-500">Helppo homma!</span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mt-2">
              Näin lounastat meillä
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-6 text-stone-600 leading-relaxed">
            <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-emerald-100 transform rotate-[0.5deg] hover:rotate-0 transition-transform">
              <span className="font-handwritten text-4xl text-emerald-500">1.</span>
              <p className="mt-2">
                Kun astut ravintolaamme, <strong className="text-emerald-600">salaattipöytä</strong> odottaa sinua ensimmäisenä. Ota lautanen ja koosta itsellesi mieluisa annos!
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-emerald-100 transform -rotate-[0.5deg] hover:rotate-0 transition-transform">
              <span className="font-handwritten text-4xl text-emerald-500">2.</span>
              <p className="mt-2">
                Sitten suuntaat <strong className="text-emerald-600">lämpimille</strong> – valitse pääruoka, keitto tai molemmat. Meillä saa ottaa lisää!
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-emerald-100 transform rotate-[0.3deg] hover:rotate-0 transition-transform">
              <span className="font-handwritten text-4xl text-emerald-500">3.</span>
              <p className="mt-2">
                Istu alas, nauti ruoasta ja päätä ateria <strong className="text-emerald-600">kahviin tai teehen</strong>. Ei kiirettä – vain hyvää ruokaa ja rauhallinen hetki ☕
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Find us - CTA + embedded map */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-600 rounded-3xl p-6 md:p-10 shadow-2xl overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 border-[10px] border-white/10 rounded-full pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 border-[10px] border-white/10 rounded-full pointer-events-none" />

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
              {/* Vasen: teksti + napit */}
              <div className="text-center md:text-left text-white">
                <span className="font-handwritten text-3xl text-emerald-100 block mb-2">
                  Tule käymään
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  Täältä löydät meidät
                </h2>
                <p className="text-emerald-50 mb-6 text-base md:text-lg">
                  <MapPin className="inline w-5 h-5 mr-1 -mt-1" />
                  {restaurantInfo.address}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      'Ravintola Data ' + restaurantInfo.address
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
                    data-testid="find-us-map-button"
                  >
                    <MapPin className="w-5 h-5" />
                    Avaa kartta
                  </a>
                  <a
                    href={`tel:${restaurantInfo.phone.replace(/\s+/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 bg-stone-900 text-white font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
                    data-testid="find-us-call-button"
                  >
                    <Phone className="w-5 h-5" />
                    Soita
                  </a>
                </div>
              </div>

              {/* Oikea: upotettu kartta */}
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white aspect-[4/3] md:aspect-auto md:h-72 bg-stone-100">
                <iframe
                  title="Ravintola Datan sijainti"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(
                    'Ravintola Data ' + restaurantInfo.address
                  )}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="find-us-map-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LounasPage;
